import React, { FormEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { isEmail } from 'validator';

import { RootState } from 'store/reducers';
import {
  addContact,
  editContact,
  generateContactAccessDocument,
  sendContactNotification,
} from 'store/actions/contactActions';
import { getCurrentUser } from 'store/actions/userActions';
import { setModal } from 'store/actions/modalActions';
import {
  config,
  dateHelper,
  optionsHelper,
  handleResponseError,
  reactToast,
} from 'modules/common/helpers';

import { Col, Row } from 'modules/bootstrap/components';
import {
  TextAreaRef,
  TextInputRef,
  SelectController,
  PhoneInput,
  BirthdayInput,
  CountryInput,
  Modal,
  GreyLinedContainer,
  Button,
} from 'modules/common/components';
import { Checkbox } from 'modules/elements/components';
import { ManageSubscriptionButton } from 'modules/subscription/components';
import { ProfileForm } from 'modules/profile/components';
import {
  ContactSubstituteSelection,
  ContactMethodSummary,
} from 'modules/contact/components';
import { SingleSelectWithOtherOption } from 'modules/assistant/components/Answers';

import { PAYMENT_STATUS, RELATIONSHIP } from 'modules/common/constants/enums';
import { ERROR_CODES, MODALS } from 'modules/common/constants';

type Inputs = {
  id?: string;
  gender: SelectOption;
  name: string;
  surname: string;
  birthday: string;
  contactNow: SelectOption;
  email: string;
  phone: string;
  personalMessage?: string;
  address?: string;
  country?: SelectOption;
  city?: string;
  zip?: string;
  relation?: SelectOption;
  substituteContactId: SelectOption;
  otherRelation?: string;
};

interface SubmitFields extends Inputs {
  passphrase?: string;
}

export interface ContactFormProps {
  contactItem?: ContactDTO;
  onClose: () => void;
  onSuccess?: (newContact: ContactDTO) => void;
  scrollToId?: string;
  onModalOverlaid?: (overlaid: boolean) => void;
  formHidden?: boolean;
}

const ContactForm = ({
  onClose,
  onSuccess,
  scrollToId,
  onModalOverlaid,
  formHidden,
  contactItem = {} as ContactDTO,
}: ContactFormProps) => {
  const { t } = useTranslation(['auth', 'common', 'contacts']);
  const {
    register,
    handleSubmit,
    control,
    setError,
    getValues,
    setValue,
    watch,

    formState: { errors },
  } = useForm<Inputs>();
  const dispatch = useDispatch();

  const currentUser: UserProfileDTO = useSelector(
    (state: RootState) => state.user.current
  );

  const [, contactByPhone, contactByPost] = contactItem.contactBy
    ?.split('')
    .map((strNum) => strNum === '1') || [true, false, false];

  const [isPhoneChecked, setIsPhoneChecked] = useState(contactByPhone);
  const [isPostChecked, setIsPostChecked] = useState(contactByPost);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHideWhenPassPhraseModal, setIsHideWhenPassPhraseModal] =
    useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);

  const isNewContact = !contactItem.id;

  const [passPhraseRequired, setPassPhraseRequired] = useState(false);
  React.useEffect(() => {
    if (onModalOverlaid) {
      onModalOverlaid(passPhraseRequired || showProfileForm);
    }
  }, [passPhraseRequired, showProfileForm]);

  const onSubmit = async (formData: SubmitFields) => {
    if (!currentUser.profile) {
      setShowProfileForm(true);
      return;
    }

    setIsSubmitting(true);
    const submitData = {
      ...contactItem,
      ...formData,
      gender: (formData?.gender?.value as string) || '',
      relation: formData[RELATIONSHIP.OTHER]
        ? formData[RELATIONSHIP.OTHER]
        : formData?.relation
        ? (formData?.relation?.value as string)
        : '',
      birthday:
        formData.birthday &&
        dateHelper.convertDateFormat(
          formData.birthday,
          config.format.serverDate
        ),
      country: (formData.country && (formData.country.value as string)) || '',
      contactNow: formData?.contactNow?.value === 'true',
      email: formData.email.toLowerCase(),
      contactBy: `1${Number(isPhoneChecked)}${Number(isPostChecked)}`,
      substituteContactId: formData?.substituteContactId?.value,
    } as ContactDTO;

    if (isNewContact) {
      const newContact: any = await dispatch(
        addContact(submitData, true, { returnError: true })
      );

      const isError = actionErrorCheck(newContact);
      if (isError) return;

      if (newContact.id) {
        const newDocumentId: any = await dispatch(
          generateContactAccessDocument(newContact.id)
        );
        if (submitData.contactNow && newDocumentId) {
          await dispatch(sendContactNotification(newContact.id));
        }
      }

      if (onSuccess && newContact) {
        onSuccess(newContact);
      }
    } else {
      const isDocumentGenerationNeeded =
        submitData.gender !== contactItem.gender ||
        submitData.birthday !== contactItem.birthday ||
        submitData.name !== contactItem.name ||
        submitData.surname !== contactItem.surname ||
        submitData.email !== contactItem.email;

      const isChangeToContactNow =
        submitData.contactNow !== contactItem.contactNow &&
        submitData.contactNow;
      const editedContact: any = await dispatch(
        editContact(submitData, true, { returnError: true })
      );

      const isError = actionErrorCheck(editedContact);
      if (isError) return;

      if (isDocumentGenerationNeeded && editedContact.id) {
        const updatedDocumentId: any = await dispatch(
          generateContactAccessDocument(editedContact.id, false)
        );
        if (isChangeToContactNow && updatedDocumentId) {
          await dispatch(sendContactNotification(editedContact.id));
        }
      } else if (isChangeToContactNow) {
        await dispatch(sendContactNotification(editedContact.id));
      }
    }

    setIsSubmitting(false);
    onClose();
  };

  const actionErrorCheck = (response) => {
    const error = handleResponseError(response);
    if (error === ERROR_CODES.NOT_ALLOWED) {
      setPassPhraseRequired(true);
      setIsHideWhenPassPhraseModal(true);
      return true;
    } else if (error) {
      reactToast.showError(t(`errors:${error}`));
      return true;
    } else return false;
  };

  const submit = (e: FormEvent) => {
    e.stopPropagation();
    e.preventDefault();

    handleSubmit(async (formData: Inputs) => {
      if (currentUser.email === formData.email.trim()) {
        setError('email', {
          message: t('auth:email_cannot_assign_yourself'),
          type: 'yourselfEmail',
        });
        return;
      }

      await onSubmit(getValues());
    })();
  };

  // handle passphrase requirement
  React.useEffect(() => {
    if (!!passPhraseRequired) {
      dispatch(
        setModal({
          name: MODALS.PASSPHRASE,
          props: {
            cancelAction: handlePassPhraseCancel,
            successAction: handlePassPhraseSuccess,
          },
        })
      );
    }
  }, [passPhraseRequired]);

  const handlePassPhraseSuccess = async () => {
    setPassPhraseRequired(false);
    const submitData = getValues() as SubmitFields;

    setIsHideWhenPassPhraseModal(false);
    await onSubmit(submitData);
  };

  const handlePassPhraseCancel = () => {
    setPassPhraseRequired(false);
    setIsSubmitting(false);
    setIsHideWhenPassPhraseModal(false);
  };

  const fieldRequired = t('common:field_required');
  const genderOptions = optionsHelper.getGenderOptions();
  const contactMethodOptions = optionsHelper.getContactMethodOptions();
  const defaultGenderOptions =
    genderOptions.find((item) => item.value === contactItem.gender) ||
    genderOptions[0];
  const defaultContactMethodOptions = contactMethodOptions.find(
    (item) => item.value === (contactItem?.contactedDate ? 'true' : 'false')
  );
  const relationshipOptions = optionsHelper.getRelationshipOptions();

  const errEmail = () => {
    if (errors.email) {
      if (errors.email.type === 'required')
        return `${t('auth:email_required')}`;
      if (errors.email.type === 'yourselfEmail') return errors.email.message;
      return `${t('auth:email_not_valid')}`;
    }
    return null;
  };

  const errPhone = () => {
    if (errors.phone) {
      return (
        errors.phone.type === 'required'
          ? `${t('auth:phone_label')} ${t('common:required')}`
          : `${t('auth:phone_label')} ${t('common:not_valid')}`
      ) as string;
    }
    return null;
  };

  const handlePhoneCheckboxClick = () => {
    setIsPhoneChecked(!isPhoneChecked);
  };

  const handlePostCheckboxClick = () => {
    setIsPostChecked(!isPostChecked);
  };

  const textFieldRequired = t('common:field_required');
  const isPaidSubscription =
    currentUser.paymentStatus === PAYMENT_STATUS.ACTIVE;

  const handleSuccessfulProfileUpdate = async () => {
    await dispatch(getCurrentUser());
    handleHideProfileModal();
  };

  const handleHideProfileModal = () => {
    setShowProfileForm(false);
  };

  React.useEffect(() => {
    ReactTooltip.rebuild();
  }, [currentUser.verified]);

  return (
    <>
      {showProfileForm && (
        <Modal show onHide={handleHideProfileModal} hasCloseCross>
          <h1 className="typo-alpha t-pb-5">
            {t('mcontacts:profile_not_complete_contact_form_title')}
          </h1>
          <div className="t-flex">
            <ProfileForm
              isProfilePage={false}
              onClose={handleHideProfileModal}
              onSuccess={handleSuccessfulProfileUpdate}
            />
          </div>
        </Modal>
      )}
      <form
        onSubmit={submit}
        className={`Form xl:t-mb-0 ${
          isHideWhenPassPhraseModal || formHidden || showProfileForm
            ? 't-hidden'
            : ''
        }`}
        data-testid="ContactForm"
      >
        {!isNewContact && (
          <input
            type="hidden"
            id="id"
            {...register('id')}
            value={contactItem.id}
          />
        )}

        <Row>
          <Col xs={6} xl={3}>
            <div className="Form-group">
              <label className="Form-label" htmlFor="gender">
                {t('auth:title')}
              </label>

              <SelectController
                id="gender"
                name="gender"
                options={genderOptions}
                control={control}
                defaultValue={defaultGenderOptions}
              />
            </div>
          </Col>
          <Col xl={4}>
            <TextInputRef
              label={t('auth:name_f')}
              type="text"
              autoComplete={false}
              defaultValue={contactItem.name}
              error={errors.name && fieldRequired}
              {...register('name', { required: true })}
            />
          </Col>

          <Col xl={5}>
            <TextInputRef
              {...register('surname', { required: true })}
              label={t('auth:name_l')}
              type="text"
              autoComplete={false}
              defaultValue={contactItem.surname}
              error={errors.surname && fieldRequired}
            />
          </Col>
        </Row>

        <Row>
          <Col xs={6} xl={4}>
            <BirthdayInput
              control={control}
              error={errors?.birthday?.message}
              defaultValue={dateHelper.convertDateFormat(
                contactItem.birthday || '',
                config.format.uiDate
              )}
              isRequired={true}
              tooltipText={t('mcontacts:contact_birthday_tooltip')}
            />
          </Col>
          <Col xl={8}>
            <SingleSelectWithOtherOption
              label={t('mcontacts:rel_field_label')}
              control={control}
              watch={watch}
              register={register}
              errors={errors}
              setValue={setValue}
              name="relation"
              selectOptions={relationshipOptions}
              defaultValue={contactItem?.relation}
              otherOptionFieldData={{
                name: RELATIONSHIP.OTHER,
                label: t('plan:subtype_other_label'),
              }}
            />
          </Col>
        </Row>

        <p className="t-pt-4 t-pb-2">{t('common:contact_method_label')}</p>
        <Row>
          <Col xl={6}>
            <div className="Form-group">
              <SelectController
                id="contactNow"
                name="contactNow"
                options={contactMethodOptions}
                control={control}
                defaultValue={defaultContactMethodOptions}
                rules={{ required: true }}
                className={`Select ${errors.contactNow ? 'isErrored' : ''}`}
              />
              {errors.contactNow && (
                <div className="t-mt-0.5">
                  <span className="t-text-sm t-text-epsilon-600">
                    {textFieldRequired}
                  </span>
                </div>
              )}
            </div>
          </Col>
          <Col className="t-pb-2">
            <p className="t-mt-1.5">{t('common:via')}</p>
          </Col>
        </Row>

        <Row>
          <Col xl={6} className="t-pb-2 t-mt-2">
            <GreyLinedContainer isLineVisible>
              <Checkbox
                text={t('common:email_required_label')}
                checked
                id="emailCheckbox"
                textClassName="text-main"
                disabled
                isBlueBg
              />
            </GreyLinedContainer>
          </Col>
          <Col xl={6}>
            <TextInputRef
              {...register('email', {
                required: true,
                validate: (value) => isEmail(value),
              })}
              onBlur={(e: React.ChangeEvent<HTMLInputElement>) =>
                setValue('email', e.target.value.trim())
              }
              type="email"
              autoComplete={false}
              defaultValue={contactItem.email}
              error={errEmail()}
              dataTestId="email-input"
            />
          </Col>
        </Row>

        <Row>
          <Col xl={6} className="t-pb-2 xl:t-h-12 t-mt-2">
            <GreyLinedContainer isLineVisible={isPhoneChecked}>
              <Checkbox
                text={t('common:phone_label')}
                checked={isPhoneChecked}
                id="phoneCheckbox"
                onChange={handlePhoneCheckboxClick}
                textClassName="text-main"
                isBlueBg
                tooltipText={t('mcontacts:contact_phone_tooltip')}
                testId="phoneCheckbox"
              />
            </GreyLinedContainer>
          </Col>
          <Col xl={6}>
            {(isPhoneChecked || !!contactItem?.phone) && (
              <PhoneInput
                control={control}
                error={errPhone()}
                defaultValue={contactItem.phone}
                isRequired={isPhoneChecked}
                isDefaultLabelVisible={false}
              />
            )}
          </Col>
        </Row>

        <Row className="t-mt-4 xl:t-mt-0">
          <Col xl={7}>
            <Checkbox
              text={t('common:post_label')}
              checked={isPostChecked}
              id="postCheckbox"
              onChange={handlePostCheckboxClick}
              disabled={!isPaidSubscription}
              textClassName="text-main"
              isBlueBg
              testId="postCheckbox"
            />
          </Col>
          <Col xl={5}>
            {!isPaidSubscription && (
              <ManageSubscriptionButton
                info={t('mcontacts:upgrade_button_tooltip')}
              />
            )}
          </Col>
        </Row>

        {(isPostChecked || !!contactItem?.address) && (
          <>
            <Row className="t-pt-4">
              <Col>
                <TextInputRef
                  {...register('address', { required: isPostChecked })}
                  placeholder={t('auth:address_label')}
                  type="text"
                  error={errors.address && textFieldRequired}
                  defaultValue={contactItem?.address}
                  inputClass="text-main"
                />
              </Col>
            </Row>
            <Row>
              <Col>
                <CountryInput
                  control={control}
                  error={errors.country}
                  defaultValue={contactItem?.country}
                  isRequired={isPostChecked}
                  refSetValue={setValue}
                  refGetValues={getValues}
                  isDefaultLabelVisible={false}
                />
              </Col>
              <Col xl={4}>
                <TextInputRef
                  {...register('city', { required: isPostChecked })}
                  placeholder={t('auth:city_label')}
                  type="text"
                  error={errors.city && textFieldRequired}
                  defaultValue={contactItem?.city}
                />
              </Col>
              <Col xl={3}>
                <TextInputRef
                  {...register('zip', { required: isPostChecked })}
                  placeholder={t('auth:postal_label')}
                  type="text"
                  error={errors.zip && textFieldRequired}
                  defaultValue={contactItem?.zip}
                />
              </Col>
            </Row>
          </>
        )}

        <Row className="t-pt-8">
          <Col>
            <TextAreaRef
              {...register('personalMessage')}
              label={t('common:user_message')}
              defaultValue={contactItem?.personalMessage}
              maxLength={2000}
              rows={3}
              placeholder={t('common:user_message_placeholder')}
              textareaClass="text-main"
              info={t('common:input_personal_message_info')}
            />
          </Col>
        </Row>
        <ReactTooltip
          id="submitBtn"
          effect="solid"
          place="top"
          multiline
          className="t-bg-eta-100 typo-epsilon t-text-zeta-600"
        />

        <ContactSubstituteSelection
          control={control}
          contactItem={contactItem}
          scrollToId={scrollToId}
        />

        <ContactMethodSummary
          watch={watch}
          phone={isPhoneChecked}
          post={isPostChecked}
        />

        <div className="t-flex t-flex-wrap t-justify-end xs:t-mt-5 sm:t-mt-0 t-z-0 t-relative">
          <Button
            category="secondary"
            className="xs:t-mt-5 xs:t-order-2 sm:t-order-1 xs:t-ml-4"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('common:cancel')}
          </Button>
          <div
            className="t-ml-5 t-self-end sm:t-mb-0 xs:t-order-1 sm:t-order-2"
            data-tip={t('notification:verifyEmailAddress_description')}
            data-tip-disable={!!currentUser.verified}
            data-for="submitBtn"
          >
            <Button
              type="submit"
              disabled={!currentUser.verified}
              loading={isSubmitting}
            >
              {t('common:save_and_continue')}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ContactForm;
